'use client';

import { useSyncedLocalStorage, writeSyncedLocalStorage } from '@/lib/syncedLocalStorage';
import { isZemaxEngine, type AnalysisEngine } from '@/types/optical';

// Admin-only merit-arm toggle. INLINE merit is the server default (the main
// agent holds the merit tools directly); turning this ON sends
// merit_inline: false on every job request, pinning the legacy SEPARATE
// merit side-agent (delegation). Mirrors the usePromptVersion localStorage
// pattern.
const MERIT_SEPARATE_KEY = 'photonium_merit_separate';
const MERIT_SEPARATE_CHANGED_EVENT = 'meritSeparateChanged';

function readStored(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(MERIT_SEPARATE_KEY) === 'true';
}

export function useMeritSeparate() {
  const meritSeparate = useSyncedLocalStorage(
    MERIT_SEPARATE_KEY,
    MERIT_SEPARATE_CHANGED_EVENT,
    readStored,
  );

  function setMeritSeparate(on: boolean): void {
    // null clears the key so "off" leaves no stale entry behind.
    writeSyncedLocalStorage(MERIT_SEPARATE_KEY, MERIT_SEPARATE_CHANGED_EVENT, on ? 'true' : null);
  }

  return { meritSeparate, setMeritSeparate };
}

export function getMeritSeparate(): boolean {
  return readStored();
}

/**
 * Why the merit-arm toggle is moot on this engine — or null when it applies.
 * The ONE gate shared by the capabilities-panel checkbox (renders the reason)
 * and startJob's flag suppression (checks === null), so the two can't drift.
 * Mirrors the server's own gate (effectiveMeritInline: Zemax-only).
 */
export function meritSeparateBlockedReason(engine: AnalysisEngine): string | null {
  if (!isZemaxEngine(engine)) {
    return 'Requires a Zemax engine — there is no merit path on Optiland.';
  }
  return null;
}
